import { Injectable } from '@angular/core';
import { EMPTY, Observable, concat, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

import { HttpService } from '~/core/services/http.service';
import { CITATIONS_API_PATHS, CITATIONS_SOURCE_CONTAINER } from './citations-api.constants';

export interface CitationSourceMetadataParams {
    sourceFileName: string;
    type: string;
    container?: string;
    rowUid?: string | null;
    rowUidColumn?: string | null;
}

interface CacheEntry<T> {
    value: T;
    cachedAt: number;
}

@Injectable({
    providedIn: 'root'
})
export class CitationsApiService {
    private readonly manifestCache = new Map<string, CacheEntry<unknown>>();
    private readonly timelineCache = new Map<string, CacheEntry<unknown>>();
    private readonly assetDetailsCache = new Map<string, CacheEntry<unknown>>();

    private readonly manifestInFlight = new Map<string, Observable<unknown>>();
    private readonly timelineInFlight = new Map<string, Observable<unknown>>();
    private readonly assetDetailsInFlight = new Map<string, Observable<unknown>>();

    constructor(private readonly httpService: HttpService) {}

    public getManifest<T>(runId: string, refinery: string, unit: string): Observable<T> {
        const scopedBase = this.getScopedCitationsBasePath(runId, refinery, unit);
        const cacheKey = this.getRunScopeKey(runId, refinery, unit);

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.manifestCache,
            this.manifestInFlight,
            cacheKey,
            () => this.httpService.Get<T>(`${scopedBase}/${CITATIONS_API_PATHS.manifest}`)
        );
    }

    public getAssetTimeline<T>(runId: string, refinery: string, unit: string, assetTag: string): Observable<T> {
        const scopedBase = this.getScopedCitationsBasePath(runId, refinery, unit);
        const cacheKey = this.getRunScopeAssetKey(runId, refinery, unit, assetTag);

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.timelineCache,
            this.timelineInFlight,
            cacheKey,
            () => this.httpService.Get<T>(
                `${scopedBase}/${CITATIONS_API_PATHS.assetTimeline}/${encodeURIComponent(assetTag.trim())}`
            )
        );
    }

    public getAssetDetails<T>(runId: string, refinery: string, unit: string, assetTag: string): Observable<T> {
        const scopedBase = this.getScopedCitationsBasePath(runId, refinery, unit);
        const cacheKey = this.getRunScopeAssetKey(runId, refinery, unit, assetTag);

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.assetDetailsCache,
            this.assetDetailsInFlight,
            cacheKey,
            () => this.httpService.Get<T>(
                `${scopedBase}/${CITATIONS_API_PATHS.assetDetails}/${encodeURIComponent(assetTag.trim())}`
            )
        );
    }

    public getSourceByMetadataBlob(
        params: CitationSourceMetadataParams,
        runId: string,
        refinery: string,
        unit: string,
        timeoutMs: number = 120000
    ): Observable<Blob> {
        return this.httpService.GetBlob(this.buildSourceMetadataEndpoint(params, runId, refinery, unit), undefined, timeoutMs);
    }

    public getDeltaSourceRows<T>(
        params: CitationSourceMetadataParams,
        runId: string,
        refinery: string,
        unit: string,
        timeoutMs: number = 120000
    ): Observable<T> {
        return this.httpService.Get<T>(
            this.buildSourceMetadataEndpoint({ ...params, type: 'delta' }, runId, refinery, unit),
            undefined,
            timeoutMs
        );
    }

    private buildSourceMetadataEndpoint(
        params: CitationSourceMetadataParams,
        runId: string,
        refinery: string,
        unit: string
    ): string {
        const scopedBase = this.getScopedCitationsBasePath(runId, refinery, unit);
        const query = new URLSearchParams({
            source_file_name: params.sourceFileName,
            container: params.container ?? CITATIONS_SOURCE_CONTAINER,
            type: params.type
        });

        // Delta rows are addressed by UID; file sources omit these entirely.
        if (params.rowUid) {
            query.set('row_uid', params.rowUid);
        }
        if (params.rowUidColumn) {
            query.set('row_uid_column', params.rowUidColumn);
        }

        return `${scopedBase}/${CITATIONS_API_PATHS.sourceFile}?${query.toString()}`;
    }

    private getWithCacheAndBackgroundRefresh<T>(
        cache: Map<string, CacheEntry<unknown>>,
        inFlight: Map<string, Observable<unknown>>,
        key: string,
        requestFactory: () => Observable<T>
    ): Observable<T> {
        const normalizedKey = key.trim();
        const cached = cache.get(normalizedKey) as CacheEntry<T> | undefined;
        const request$ = this.getOrCreateInFlightRequest<T>(cache, inFlight, normalizedKey, requestFactory);

        if (!cached) {
            return request$;
        }

        return concat(
            of(cached.value),
            request$.pipe(
                // Keep fast view rendering from cache if refresh fails.
                catchError(() => EMPTY)
            )
        );
    }

    private getOrCreateInFlightRequest<T>(
        cache: Map<string, CacheEntry<unknown>>,
        inFlight: Map<string, Observable<unknown>>,
        key: string,
        requestFactory: () => Observable<T>
    ): Observable<T> {
        const existing = inFlight.get(key) as Observable<T> | undefined;

        if (existing) {
            return existing;
        }

        const request$ = requestFactory().pipe(
            tap((response) => {
                cache.set(key, {
                    value: response,
                    cachedAt: Date.now()
                });
            }),
            finalize(() => inFlight.delete(key)),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        inFlight.set(key, request$ as Observable<unknown>);
        return request$;
    }

    private getScopedCitationsBasePath(runId: string, refinery: string, unit: string): string {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();

        return `${CITATIONS_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/${CITATIONS_API_PATHS.units}/${encodeURIComponent(normalizedUnit)}/${CITATIONS_API_PATHS.runs}/${encodeURIComponent(normalizedRunId)}/${CITATIONS_API_PATHS.citations}`;
    }

    private getRunScopeKey(runId: string, refinery: string, unit: string): string {
        return `${runId.trim()}::${refinery.trim().toUpperCase()}::${unit.trim().toUpperCase()}`;
    }

    private getRunScopeAssetKey(runId: string, refinery: string, unit: string, assetTag: string): string {
        return `${this.getRunScopeKey(runId, refinery, unit)}::${assetTag.trim()}`;
    }
}
